import React, { useEffect } from 'react'
import * as api from '../../api'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {FD100MultiSelectOptions} from '../../Plateforme/Utils/Listings';
import FabFrIndicator from '../../Plateforme/Atoms/FabFrIndicator';
import { getEventDesignerDisplayName, getEventDesignerProfileLink } from './utils/eventDesignerHelpers';
import BoldBlue from '../../Plateforme/Atoms/BoldBlue';
import H1 from '../../Plateforme/Atoms/H1';
import H2 from '../../Plateforme/Atoms/H2';

const RatingLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-top: 1px solid #ccc;
    font-size: 13px;
    transition: background-color 0.3s ease;

    &.voted {
        background-color: #d4edda;
        border-color: #c3e6cb;
        
        &:hover {
            background-color: #c3e6cb;
        }
    }

    > .projinfo {
        width: 30%;
        flex-grow: 1;

        a{
            color: black;
            text-decoration: none;
            cursor: pointer;
            &:hover{
                color: blue;
            }
        }
    }

    > .stars {
        width: 70px;
        margin: 0 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 20px;

        > span {
            cursor: pointer;
            color: #ffdb00;
            font-weight: 200;
        }
    }

    > label {
        display: flex;
        align-items: center;
        margin: 0 10px;

        > span {
            margin-right: 10px;
        }

        > input {
            margin: 0;
        }
    }
`

const TableHeader = styled.div`
    margin-left: 50%;
    height: 100px;
    position: relative;

    > span {
        position: absolute;
        transform: rotate(-45deg);
        transform-origin: 0;
        bottom:0;
        left: 0;
    }
`

const StatsContainer = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .stats-item {
        text-align: center;
        
        .number {
            font-size: 2em;
            font-weight: bold;
            color: #0000ff;
        }
        
        .label {
            font-size: 0.9em;
            color: #6c757d;
            margin-top: 5px;
        }
    }
    
    .progress-container {
        flex: 1;
        margin: 0 30px;
        
        .progress-bar {
            width: 100%;
            height: 20px;
            background: #e9ecef;
            border-radius: 10px;
            overflow: hidden;
            
            .progress-fill {
                height: 100%;
                background: #0000ff;
                transition: width 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 0.8em;
                font-weight: bold;
            }
        }
        
        .progress-label {
            text-align: center;
            margin-top: 8px;
            font-size: 0.9em;
            color: #6c757d;
        }
    }
`

const SaveNotification = styled.div`
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: ${props => props.status === 'loading' ? '#0000ff' : props.status === 'saved' ? '#28a745' : '#dc3545'};
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    transition: all 0.3s ease;
    opacity: ${props => props.show ? 1 : 0};
    transform: translateX(-50%) translateY(${props => props.show ? '0' : '20px'});
    
    @keyframes fadeOut {
        0% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(20px); }
    }
    
    .spinner {
        margin-right: 10px;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top: 2px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    .checkmark {
        margin-right: 8px;
        font-size: 18px;
    }
`

const CategoryHeader = styled.div`
    color: white;
    padding: 8px 12px;
    margin: 15px 0 0 0;
    border-radius: 4px;
    font-weight: 500;
    font-size: 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    
    &.objet-category {
        background: linear-gradient(135deg,rgb(228, 209, 0),rgb(255, 243, 8));
    }
    
    &.espace-category {
        background: linear-gradient(135deg, #fd7e14, #e55100);
    }
`

const FilterContainer = styled.div`
    background: #fff;
    padding: 15px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    
    .filter-group {
        display: flex;
        align-items: center;
        gap: 10px;
        
        .filter-label {
            font-weight: 600;
            color: #495057;
        }
        
        .star-filter {
            display: flex;
            gap: 5px;
            
            button {
                border: 2px solid #0000ff;
                background: white;
                color: #0000ff;
                padding: 5px 10px;
                border-radius: 20px;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s ease;
                
                &.active {
                    background: #0000ff;
                    color: white;
                }
                
                &:hover {
                    background: #0000ff;
                    color: white;
                }
            }
        }
        
        .search-input {
            padding: 8px 12px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            width: 250px;
            font-size: 14px;
            
            &:focus {
                outline: none;
                border-color: #0000ff;
                box-shadow: 0 0 0 0.2rem rgba(0,0,255,.25);
            }
        }
    }
`

const JuryProfile = (props) => {

    const { i18n } = useTranslation('common');
    const navigate = useNavigate();
    const [event, setEvent] = React.useState({});
    // const [allParticipations, setAllParticipations] = React.useState([]);
    const [allProjects, setAllProjects] = React.useState([]);
    const [ratings , setRatings] = React.useState([]);
    const [dataStatus, setDataStatus] = React.useState("loading");
    const [starFilter, setStarFilter] = React.useState("all");
    const [searchFilter, setSearchFilter] = React.useState("");
    const [showSaveNotification, setShowSaveNotification] = React.useState(false);
    const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

    //get event
    useEffect(() => {
        if (props.user.eventId) {
            api.getEventById({ id: props.user.eventId }).then((response) => {
                console.log(response.data);
                setEvent(response.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [props.user.eventId])

    //get all participations
    useEffect(() => {
        if (event._id) {
            api.getParticipatingUsersWithData({ eventId: event._id }).then((response) => {
                console.log(response.data);
                let _allProjetcs = []
                response.data.forEach(user => {
                    if (user.participation.data.projets) {
                        user.participation.data.projets.forEach(project_id => {

                            const projectObject = user.company.projects.find(p => p._id === project_id);
                            if (!projectObject) {
                                return;
                            }

                            if(projectObject.status === "validé"){
                                projectObject.participationId = user.participation._id;
                                _allProjetcs.push({ projet: projectObject, user: user })
                            }
                        })
                    }
                })
                console.log("_allProjetcs");
                console.log(_allProjetcs);
                setAllProjects(_allProjetcs)
                setDataStatus("saved");
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [event._id])

    //get jury's ratings
    React.useEffect(() => {
        getUserRatings();
    },[]);

    async function getUserRatings() {
        api.GetCurrentUser().then((response) => {
            console.log(response.data)
            setRatings(response.data.juryNotes || []);
        }).catch((error) => {
            console.log(error);
            navigate('/');
        });
    }

    //save ratings whenever they change
    React.useEffect(() => {
        if(dataStatus === "unsaved"){
            setDataStatus("loading");
            if(hasUserInteracted) {
                setShowSaveNotification(true);
            }
            console.log("saving ratings");
            console.log(ratings);
            api.setRatings(props.user._id,ratings).then(() => {
                setDataStatus("saved");
                if(hasUserInteracted) {
                    // Hide notification after 3 seconds
                    setTimeout(() => {
                        setShowSaveNotification(false);
                    }, 3000);
                }
            });
        }
    },[ratings, dataStatus, hasUserInteracted]);

    async function setStar(projet,starNumber){
        setHasUserInteracted(true);
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);
        if(_ratingIndex !== -1){
            _ratings[_ratingIndex].stars = starNumber;
        }else{
            _ratings.push({participationId : projet.projet.participationId, projetId: projet.projet._id, stars: starNumber})
        }
        setRatings(_ratings);
        setDataStatus("unsaved");
    }

    async function setCheckbox(projet,e){
        setHasUserInteracted(true);
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);
        if(_ratingIndex !== -1){
            _ratings[_ratingIndex][e.target.name] = e.target.checked ? "checked" : "";
        }else{
            _ratings.push({participationId : projet.projet.participationId, projetId: projet.projet._id, [e.target.name]: e.target.checked ? "checked" : ""})
        }
        setRatings(_ratings);
        setDataStatus("unsaved");
    }

    async function setCommentaire(projet,e){
        setHasUserInteracted(true);
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);
        if(_ratingIndex !== -1){
            _ratings[_ratingIndex].commentaire = e.target.value;
        }else{
            _ratings.push({participationId : projet.projet.participationId, projetId: projet.projet._id, commentaire: e.target.value})
        }
        setRatings(_ratings);
        setDataStatus("unsaved");
    }

    // async function saveRatings(){
    //     setDataStatus("loading");
    //     api.setRatings(props.user._id,ratings).then((response) => {
    //         console.log(response.data)
    //         if(response.data === "ok"){
    //             setDataStatus("saved");
    //         }
    //         else{
    //             alert("error");
    //         }
    //     }).catch((error) => {
    //         console.log(error);
    //     });
    // }

    const tyoplogieEspaceFD100 = FD100MultiSelectOptions.tyoplogieEspaceFD100(i18n.language);
    const typologieObjetFD100 = FD100MultiSelectOptions.typologieObjetFD100(i18n.language);

    //there are two types of categories, espace and objet, we merge them, but we need to remove the duplicates
    const FDCategories = [
        ...tyoplogieEspaceFD100,
        ...typologieObjetFD100,
    ].filter((cat, index, self) => self.findIndex((t) => t.value === cat.value) === index)

    console.log("FDCategories",FDCategories);

    // Helper function to determine if a category is OBJET or ESPACE
    const getCategoryType = (categoryValue) => {
        const isObjetCategory = typologieObjetFD100.some(cat => cat.value === categoryValue);
        return isObjetCategory ? 'objet' : 'espace';
    };

    // Category display mapping for visual merging
    const categoryDisplayMapping = {
        // ESPACE categories
        'hotel': { displayName: 'Hotel', order: 1, type: 'espace' },
        'residence': { displayName: 'Private Residence', order: 2, type: 'espace' },
        'retail': { displayName: 'Retail/Office', order: 4, type: 'espace' },
        'office': { displayName: 'Retail/Office', order: 4, type: 'espace' },
        'culturel': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'install': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'sceno': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'archi': { displayName: 'Architecture', order: 6, type: 'espace' },
        
        // OBJET categories
        'assise': { displayName: 'Seatings', order: 7, type: 'objet' },
        'table': { displayName: 'Table', order: 8, type: 'objet' },
        'luminaire': { displayName: 'Lighting', order: 9, type: 'objet' },
        'collection': { displayName: 'Collection', order: 10, type: 'objet' },
        'armoire': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'bibliotheque': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'etagere': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'buffet': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'console': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'miroir': { displayName: 'Accessories', order: 12, type: 'objet' },
        'paravent': { displayName: 'Accessories', order: 12, type: 'objet' },
        'tapis': { displayName: 'Accessories', order: 12, type: 'objet' },
        'accesoire': { displayName: 'Accessories', order: 12, type: 'objet' },
        
        // Other categories (keep as is)
        'restaurant': { displayName: 'Restaurant', order: 3, type: 'espace' },
        'urbain': { displayName: 'Urban space', order: 13, type: 'espace' },
        'commode': { displayName: 'Commode', order: 14, type: 'objet' },
        'portemanteau': { displayName: 'Portemanteau', order: 15, type: 'objet' },
        'autre': { displayName: 'Autre', order: 16, type: 'any' }
    };

    // Get display information for a category
    const getCategoryDisplayInfo = (categoryValue) => {
        return categoryDisplayMapping[categoryValue] || {
            displayName: categoryValue,
            order: 999,
            type: getCategoryType(categoryValue)
        };
    };

    // Group projects by display categories
    const getDisplayGroups = () => {
        const displayGroups = {};
        
        allProjects.forEach(project => {
            const displayInfo = getCategoryDisplayInfo(project.projet.typologie);
            const displayName = displayInfo.displayName;
            
            if (!displayGroups[displayName]) {
                displayGroups[displayName] = {
                    displayName,
                    order: displayInfo.order,
                    type: displayInfo.type,
                    categoryValues: new Set(),
                    projects: []
                };
            }
            
            displayGroups[displayName].categoryValues.add(project.projet.typologie);
            displayGroups[displayName].projects.push(project);
        });
        
        // Convert to array and sort by order
        return Object.values(displayGroups)
            .sort((a, b) => a.order - b.order);
    };

    // Get label for display group
    const getDisplayGroupLabel = (displayGroup) => {
        // For merged categories, use the custom display name
        if (displayGroup.categoryValues.size > 1) {
            return displayGroup.displayName;
        }
        
        // For single categories, use the original translation
        const categoryValue = Array.from(displayGroup.categoryValues)[0];
        const category = FDCategories.find(cat => cat.value === categoryValue);
        return category ? category.label : displayGroup.displayName;
    };

    // Filter projects based on search and star filters
    const getFilteredProjects = (projects) => {
        return projects.filter(project => {
            // Search filter - include project name, designer name, and category/typology
            const projectCategory = FDCategories.find(cat => cat.value === project.projet.typologie);
            const categoryLabel = projectCategory ? projectCategory.label : '';
            const displayInfo = getCategoryDisplayInfo(project.projet.typologie);
            const categoryType = displayInfo.type;
            
            const matchesSearch = searchFilter === "" || 
                project.projet.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                getEventDesignerDisplayName(project.projet, project.user).toLowerCase().includes(searchFilter.toLowerCase()) ||
                categoryLabel.toLowerCase().includes(searchFilter.toLowerCase()) ||
                displayInfo.displayName.toLowerCase().includes(searchFilter.toLowerCase()) ||
                categoryType.toLowerCase().includes(searchFilter.toLowerCase()) ||
                (categoryType === 'objet' && 'objet'.includes(searchFilter.toLowerCase())) ||
                (categoryType === 'espace' && 'espace'.includes(searchFilter.toLowerCase()));

            // Star filter
            const projectRating = ratings.find(rating => 
                rating.participationId === project.projet.participationId && 
                rating.projetId === project.projet._id
            );
            
            let matchesStarFilter = true;
            if (starFilter === "1") {
                matchesStarFilter = projectRating?.stars === 1;
            } else if (starFilter === "2") {
                matchesStarFilter = projectRating?.stars === 2;
            } else if (starFilter === "3") {
                matchesStarFilter = projectRating?.stars === 3;
            } else if (starFilter === "unrated") {
                matchesStarFilter = !projectRating?.stars || projectRating.stars === 0;
            }

            return matchesSearch && matchesStarFilter;
        });
    };

    // Calculate statistics
    const totalProjects = allProjects.length;
    const votedProjects = allProjects.filter(project => 
        ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars > 0
    ).length;
    const totalFavorites = ratings.filter(rating => rating.Coup_de_coeur === "checked").length;
    const progressPercentage = totalProjects > 0 ? Math.round((votedProjects / totalProjects) * 100) : 0;

    return (
        <div>
            <H2>{event.title_fr}</H2>
            <span>{i18n.language === "fr" ? "Voici la liste des candidats pré-sélectionnés pour le FD100" : "Here is the list of pre-selected candidates for Le FD100"}</span>
            <br/><br/>
            <StatsContainer>
                <div className="stats-item">
                    <div className="number">{totalProjects}</div>
                    <div className="label">
                        {i18n.language === "fr" ? "Projets total" : "Total projects"}
                    </div>
                </div>
                
                <div className="progress-container">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{width: `${progressPercentage}%`}}>
                            {progressPercentage > 15 && `${progressPercentage}%`}
                        </div>
                    </div>
                    <div className="progress-label">
                        {i18n.language === "fr" 
                            ? `${votedProjects} sur ${totalProjects} projets votés` 
                            : `${votedProjects} of ${totalProjects} projects voted`
                        }
                    </div>
                </div>
                
                <div className="stats-item">
                    <div className="number">{totalFavorites}</div>
                    <div className="label">
                        {i18n.language === "fr" ? "Coups de cœur" : "Favorites"}
                    </div>
                </div>
            </StatsContainer>

            <FilterContainer>
                <div className="filter-group">
                    <span className="filter-label">
                        {i18n.language === "fr" ? "Recherche :" : "Search:"}
                    </span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={i18n.language === "fr" ? "Rechercher un projet, designer ou catégorie..." : "Search project, designer or category..."}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />
                </div>
                
                <div className="filter-group">
                    <span className="filter-label">
                        {i18n.language === "fr" ? "Filtrer par étoiles :" : "Filter by stars:"}
                    </span>
                    <div className="star-filter">
                        <button 
                            className={starFilter === "all" ? "active" : ""}
                            onClick={() => setStarFilter("all")}
                        >
                            {i18n.language === "fr" ? "Tout" : "All"}
                        </button>
                        <button 
                            className={starFilter === "unrated" ? "active" : ""}
                            onClick={() => setStarFilter("unrated")}
                        >
                            {i18n.language === "fr" ? "Non noté" : "Unrated"}
                        </button>
                        <button 
                            className={starFilter === "1" ? "active" : ""}
                            onClick={() => setStarFilter("1")}
                        >
                            1★
                        </button>
                        <button 
                            className={starFilter === "2" ? "active" : ""}
                            onClick={() => setStarFilter("2")}
                        >
                            2★
                        </button>
                        <button 
                            className={starFilter === "3" ? "active" : ""}
                            onClick={() => setStarFilter("3")}
                        >
                            3★
                        </button>
                    </div>
                </div>
            </FilterContainer>

            {/* <TableHeader>
                <span>{i18n.language === "fr" ? "Votre note" : "Your rating"}</span>
                <span style={{left:"70px"}}>{i18n.language === "fr" ? "Coup de Coeur" : "Coup de Coeur"}</span>
                <span style={{left:"115px"}}>{i18n.language === "fr" ? "Art de vivre à la française" : "French art of living"}</span>
                <span style={{left:"160px"}}>{i18n.language === "fr" ? "Innovation / Durabilité" : "Innovation / Sustainability"}</span>
            </TableHeader> */}
            {getDisplayGroups().filter(group => getFilteredProjects(group.projects).length > 0).map((group,i) => 
                <div key={i}>
                    <CategoryHeader className={`${group.type}-category`}>
                        <span style={{textTransform: 'uppercase', fontSize: '11px', fontWeight: 'bold', opacity: 0.9}}>
                            {group.type === 'objet' ? (i18n.language === "fr" ? "OBJET" : "OBJECT") : (i18n.language === "fr" ? "ESPACE" : "SPACE")}
                        </span>
                        <span style={{marginLeft: '12px', fontSize: '14px'}}>
                            {i18n.language === "fr" ? "Catégorie" : "Category"} {i%6 + 1}_{getDisplayGroupLabel(group)}
                        </span>
                    </CategoryHeader>
                    {getFilteredProjects(group.projects).sort((a, b) => {
                        const displayNameA = getEventDesignerDisplayName(a.projet, a.user).toLowerCase();
                        const displayNameB = getEventDesignerDisplayName(b.projet, b.user).toLowerCase();
                        return displayNameA.localeCompare(displayNameB);
                    }).map((project,j) => {
                        const projectRating = ratings.find(rating => 
                            rating.participationId === project.projet.participationId && 
                            rating.projetId === project.projet._id
                        );
                        const isVoted = projectRating?.stars > 0;
                        
                        return <RatingLine key={j} className={isVoted ? 'voted' : ''}>
                            <div className='projinfo'>
                                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                                    <div>
                                        <div style={{display:"flex", alignItems:"center"}}>
                                            <a style={{fontWeight:"100"}} href={"/admin/events/fdficheprojet?email="+project.user.user.email+"&pid="+project.user.participation._id+"&projid="+project.projet._id+"&mode=jury"} target="_blank" rel="noreferrer">{i18n.language === "fr" ? "Projet en competition" : "Project in competition"} : <BoldBlue style={{textTransform: "capitalize", fontSize: "14px"}}>{project.projet.name.toLowerCase()}</BoldBlue></a>
                                        </div>
                                        <a href={getEventDesignerProfileLink(project.projet, project.user)} target="_blank" rel="noreferrer">
                                            <span>Studio : {getEventDesignerDisplayName(project.projet, project.user)}</span>
                                        </a><br/>
                                    </div>
                                    {/* <span onClick={()=>window.open()} className='thin'>{project.projet.name}</span> */}

                                    {project.projet.fabrication_fr === "oui" && <FabFrIndicator/>}
                                </div>
                            </div>
                            <div className='stars'>
                                {/* <span style={{color:"black", fontSize:"13px"}}>{i18n.language === "fr" ? "Votre note" : "Your rating"}</span> */}
                                <span onClick={() => ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars === 1 ? setStar(project,0) : setStar(project,1)}>{ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars >= 1 ? "★" : "☆"}</span>
                                <span onClick={() => setStar(project,2)}>{ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars >= 2 ? "★" : "☆"}</span>
                                <span onClick={() => setStar(project,3)}>{ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars >= 3 ? "★" : "☆"}</span>
                            </div>
                            <label>
                                <span>{i18n.language === "fr" ? "Coup de Coeur" : "Love it"}&nbsp;:&nbsp;</span>
                                <input onChange={(e) => setCheckbox(project,e)} type="checkbox" name="Coup_de_coeur" checked={ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.Coup_de_coeur === "checked"}/>
                            </label>
                            {/* <label>
                                <span>{i18n.language === "fr" ? "Art de vivre à la française" : "French art of living"}&nbsp;:&nbsp;</span>
                                <input onChange={(e) => setCheckbox(project,e)} type="checkbox" name="Art_de_vivre_à_la_française" checked={ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.Art_de_vivre_à_la_française === "checked"}/>
                            </label>
                            <label>
                                <span>{i18n.language === "fr" ? "Innovation / Durabilité" : "Innovation / Sustainability"}&nbsp;:&nbsp;</span>
                                <input onChange={(e) => setCheckbox(project,e)} type="checkbox" name="Innovation_durabilité" checked={ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.Innovation_durabilité === "checked"}/>
                            </label> */}
                            <input style={{width:"50%"}} onChange={(e) => setCommentaire(project,e)} type="text" placeholder={i18n.language === "fr" ? "Commentaire" : "Comment"} value={ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.commentaire || ""}/>
                        </RatingLine>
                    })}
                </div>
            )}
            
            <SaveNotification status={dataStatus} show={showSaveNotification}>
                {dataStatus === "loading" && (
                    <>
                        <div className="spinner"></div>
                        {i18n.language === "fr" ? "Enregistrement en cours..." : "Saving..."}
                    </>
                )}
                {dataStatus === "saved" && (
                    <>
                        <span className="checkmark">✓</span>
                        {i18n.language === "fr" ? "Enregistrement automatique réussi" : "Auto-save successful"}
                    </>
                )}
            </SaveNotification>
        </div>
    );
};

export default JuryProfile;